import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationItem,
    FuseNavigationService,
    FuseVerticalNavigationAppearance,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { ApprovalInboxButtonComponent } from 'app/layout/common/approval-inbox-button/approval-inbox-button.component';
import { RemindersHeaderButtonComponent } from 'app/layout/common/reminders-header-button/reminders-header-button.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { filter, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { AuthService } from 'app/core/auth/auth.service';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { PermissionService, MODULE_PERMISSION_KEYS, ModuleKey } from 'app/core/permission/permission.service';
import { HospitalityAccessService } from 'app/modules/hospitality/hospitality-access.service';
import { NoPermissionsComponent } from 'app/layout/common/no-permissions/no-permissions.component';
import { SidebarAccountComponent } from 'app/layout/common/sidebar-account/sidebar-account.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ScbTenantBillingBannerComponent } from 'app/modules/service-billing/my-billing/scb-tenant-billing-banner.component';
import { ScbSettingsService } from 'app/modules/service-billing/scb-settings.service';
import { DiSettingsCacheService } from 'app/modules/digital-invoicing/di-settings-cache.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        // FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        ApprovalInboxButtonComponent,
        RemindersHeaderButtonComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        // LanguagesComponent,
        SidebarAccountComponent,
        RouterModule,
        // SearchComponent,
        // ShortcutsComponent,
        // MessagesComponent,
        RouterOutlet,
        MatBadgeModule,
        MatTooltipModule,
        MatTabsModule,
        NoPermissionsComponent,
        ScbTenantBillingBannerComponent,
        // QuickChatComponent,
    ],
    styles: [`
        :host {
            display: flex;
            flex: 1 1 auto;
            flex-direction: row;
            width: 100%;
            min-width: 0;
            min-height: 0;
            height: 100%;
            overflow: hidden;
        }

        .active-module {
            background-color: rgba(59, 130, 246, 0.15) !important;
            border-color: #3b82f6 !important;
            font-weight: 600 !important;
        }

        .dark .active-module {
            background-color: rgba(147, 197, 253, 0.15) !important;
            border-color: #93c5fd !important;
        }

        /* THE page scrollport for details / dashboards / forms.
           Must be display:block (not flex column) so tall content grows scrollHeight.
           List pages absolute-fill this pane and scroll inside the card instead. */
        .classy-main-content {
            position: relative !important;
            display: block !important;
            min-height: 0 !important;
            flex: 1 1 0% !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
        }

        .classy-main-content > router-outlet + * {
            display: block !important;
            position: relative !important;
            width: 100% !important;
            min-width: 0 !important;
            min-height: 100% !important;
            height: auto !important;
            overflow: visible !important;
        }
    `]
})
export class ClassyLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly SIDEBAR_APPEARANCE_KEY = 'bizcare-sidebar-appearance';

    @ViewChild('moduleNav') moduleNav?: ElementRef<HTMLElement>;

    isScreenSmall: boolean = false;
    /** My Home is full-width: keep module header, hide left sidebar. */
    hideSidebar: boolean = false;
    navigationAppearance: FuseVerticalNavigationAppearance = 'default';
    navigation: Navigation | null = null;
    user: User | null = null;
    inboxCount = 0;
    currentNavigation: FuseNavigationItem[] = [];
    activeModule: ModuleKey = 'accounting';
    canScrollModulesLeft = false;
    canScrollModulesRight = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _moduleNavResizeObserver?: ResizeObserver;
    /** All module definitions with permission key for access control. */
    modules = [
        { key: 'accounting', label: 'Accounting', icon: 'heroicons_outline:calculator', link: '/accounting/dashboard', permissionKey: MODULE_PERMISSION_KEYS.accounting },
        { key: 'treasury', label: 'Treasury', icon: 'heroicons_outline:banknotes', link: '/treasury/dashboard', permissionKey: MODULE_PERMISSION_KEYS.treasury },
        { key: 'inventory', label: 'Inventory', icon: 'heroicons_outline:archive-box', link: '/inventory/dashboard', permissionKey: MODULE_PERMISSION_KEYS.inventory },
        { key: 'purchases', label: 'Purchase', icon: 'heroicons_outline:shopping-cart', link: '/purchases/dashboard', permissionKey: MODULE_PERMISSION_KEYS.purchases },
        { key: 'procurement', label: 'Procurement', icon: 'heroicons_outline:document-text', link: '/procurement/dashboard', permissionKey: MODULE_PERMISSION_KEYS.procurement },
        { key: 'ops', label: 'Operations', icon: 'heroicons_outline:briefcase', link: '/ops/dashboard', permissionKey: MODULE_PERMISSION_KEYS.ops },
        { key: 'sales', label: 'Sales', icon: 'heroicons_outline:shopping-bag', link: '/sales/dashboard', permissionKey: MODULE_PERMISSION_KEYS.sales },
        { key: 'commission', label: 'Commission', icon: 'heroicons_outline:currency-dollar', link: '/commission/dashboard', permissionKey: MODULE_PERMISSION_KEYS.commission },
        { key: 'serviceBilling', label: 'Service Billing', icon: 'heroicons_outline:receipt-percent', link: '/service-billing/recovery', permissionKey: MODULE_PERMISSION_KEYS.serviceBilling },
        { key: 'digitalInvoicing', label: 'Digital Invoicing', icon: 'heroicons_outline:document-check', link: '/digital-invoicing/dashboard', permissionKey: MODULE_PERMISSION_KEYS.digitalInvoicing },
        { key: 'commodity', label: 'Commodity', icon: 'heroicons_outline:cube', link: '/commodity/dashboard', permissionKey: MODULE_PERMISSION_KEYS.commodity },
        { key: 'pos', label: 'POS', icon: 'heroicons_outline:shopping-cart', link: '/pos/dashboard', permissionKey: MODULE_PERMISSION_KEYS.pos, showInHeader: false },
        { key: 'hospitality', label: 'Hospitality', icon: 'heroicons_outline:building-office', link: '/hospitality/front-desk', permissionKey: MODULE_PERMISSION_KEYS.hospitality },
        { key: 'manufacturing', label: 'Manufacturing', icon: 'heroicons_outline:cog-8-tooth', link: '/manufacturing/dashboard', permissionKey: MODULE_PERMISSION_KEYS.manufacturing },
        { key: 'hr', label: 'HR', icon: 'heroicons_outline:user-group', link: '/hr/dashboard', permissionKey: MODULE_PERMISSION_KEYS.hr },
        { key: 'ess', label: 'ESS', icon: 'heroicons_outline:user-circle', link: '/ess/home', permissionKey: MODULE_PERMISSION_KEYS.ess },
        { key: 'logistics', label: 'Logistics', icon: 'heroicons_outline:truck', link: '/logistics/dashboard', permissionKey: MODULE_PERMISSION_KEYS.logistics },
        { key: 'gate', label: 'Gate', icon: 'heroicons_outline:building-office-2', link: '/gate/dashboard', permissionKey: MODULE_PERMISSION_KEYS.gate },
        { key: 'consignment', label: 'Consignment', icon: 'heroicons_outline:archive-box-arrow-down', link: '/consignment/dashboard', permissionKey: MODULE_PERMISSION_KEYS.consignment },
        { key: 'checklists', label: 'Checklists', icon: 'heroicons_outline:clipboard-document-check', link: '/checklists/dashboard', permissionKey: MODULE_PERMISSION_KEYS.checklists },
        { key: 'quality', label: 'Quality', icon: 'heroicons_outline:beaker', link: '/quality/dashboard', permissionKey: MODULE_PERMISSION_KEYS.quality },
        { key: 'support', label: 'Support', icon: 'heroicons_outline:lifebuoy', link: '/support/tickets', permissionKey: MODULE_PERMISSION_KEYS.support, showInHeader: false },
        { key: 'supportCenter', label: 'Support Center', icon: 'heroicons_outline:rocket-launch', link: '/support-center/dashboard', permissionKey: MODULE_PERMISSION_KEYS.supportCenter },
        { key: 'settings', label: 'Settings', icon: 'heroicons_outline:cog-6-tooth', link: '/settings/fiscal-year', permissionKey: MODULE_PERMISSION_KEYS.settings },
        { key: 'admin', label: 'Administration', icon: 'heroicons_outline:shield-check', link: '/admin/roles', permissionKey: MODULE_PERMISSION_KEYS.admin },
    ];
    /** Modules the current user is allowed to see (filtered by company + user permissions). */
    allowedModules: { key: string; label: string; icon: string; link: string; permissionKey: string; showInHeader?: boolean }[] = [];
    /** Modules shown in the header bar (excludes items like Support in the user menu). */
    headerModules: { key: string; label: string; icon: string; link: string; permissionKey: string; showInHeader?: boolean }[] = [];
    /** True when user has no module access (and is not super admin) — show no-permissions page. */
    showNoPermissions = false;
    /** Service Billing module flag from GET /api/scb/settings. */
    private scbEnabled = false;
    /** Digital Invoicing FBR environment is Sandbox (GET /api/di/settings). */
    private diFbrSandbox = false;
    private static readonly AUTH_ROUTE_SEGMENTS = new Set([
        'sign-in',
        'sign-up',
        'select-company',
        'select-branch',
        'forgot-password',
        'reset-password',
        'confirmation-required',
        'sign-out',
    ]);
    /** Top-level routes that are not module prefixes (no sidebar module switch / redirect). */
    private static readonly GLOBAL_ROUTE_SEGMENTS = new Set([
        'company-settings',
        'document-edit-requests',
        'activity-logs',
        'session-management',
        'my-home',
    ]);

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _permissionService: PermissionService,
        private _scbSettings: ScbSettingsService,
        private _diSettings: DiSettingsCacheService,
        private _hospitalityAccess: HospitalityAccessService
    ) { }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    /** BizCare logo target: SuperAdmin → My Home, others → Accounting dashboard. */
    get isSuperAdmin(): boolean {
        return this._permissionService.isSuperAdmin();
    }

    get homeLink(): string {
        return this.isSuperAdmin ? '/my-home' : '/accounting/dashboard';
    }

    ngOnInit(): void {
        const savedAppearance = localStorage.getItem(ClassyLayoutComponent.SIDEBAR_APPEARANCE_KEY);
        if (savedAppearance === 'default' || savedAppearance === 'dense') {
            this.navigationAppearance = savedAppearance;
        }

        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                this.applyPermissionsAndResolve();
            });

        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this._hospitalityAccess.refresh();
                this.applyPermissionsAndResolve();
            });

        this._hospitalityAccess.allowed$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this.applyPermissionsAndResolve());

        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        )
            .subscribe((event: NavigationEnd) => {
                this.updateHideSidebar(event.urlAfterRedirects);
                this.applyPermissionsAndResolve();
                this.resolveNavigationFromUrl(event.urlAfterRedirects);
                this.scheduleModuleNavUpdate(true);
            });

        this.updateHideSidebar(this._router.url);


        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
                this.scheduleModuleNavUpdate();
            });

        this._scbSettings
            .isEnabled$()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((enabled) => {
                this.scbEnabled = enabled;
                if (this.activeModule === 'serviceBilling') {
                    this.resolveNavigationFromUrl(this._router.url);
                }
            });

        this._diSettings
            .isFbrSandbox$()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((sandbox) => {
                if (sandbox == null) return;
                this.diFbrSandbox = sandbox;
                if (this.activeModule === 'digitalInvoicing') {
                    this.resolveNavigationFromUrl(this._router.url);
                }
                const path = this._router.url.split('?')[0];
                if (!sandbox && path.startsWith('/digital-invoicing/sandbox')) {
                    this._router.navigateByUrl('/digital-invoicing/dashboard');
                }
            });
    }

    ngAfterViewInit(): void {
        this.setupModuleNavObserver();
        this.scheduleModuleNavUpdate(true);
    }

    onModuleNavScroll(): void {
        this.updateModuleNavScrollState();
    }

    scrollModules(direction: 'left' | 'right'): void {
        const el = this.moduleNav?.nativeElement;
        if (!el) {
            return;
        }

        const amount = Math.max(180, Math.floor(el.clientWidth * 0.65));
        el.scrollBy({
            left: direction === 'right' ? amount : -amount,
            behavior: 'smooth',
        });
    }

    private setupModuleNavObserver(): void {
        const el = this.moduleNav?.nativeElement;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }

        this._moduleNavResizeObserver?.disconnect();
        this._moduleNavResizeObserver = new ResizeObserver(() => {
            this.updateModuleNavScrollState();
        });
        this._moduleNavResizeObserver.observe(el);
    }

    private scheduleModuleNavUpdate(scrollActiveIntoView = false): void {
        requestAnimationFrame(() => {
            if (scrollActiveIntoView) {
                this.scrollActiveModuleIntoView();
            }
            this.updateModuleNavScrollState();
        });
    }

    private updateModuleNavScrollState(): void {
        const el = this.moduleNav?.nativeElement;
        if (!el) {
            this.canScrollModulesLeft = false;
            this.canScrollModulesRight = false;
            return;
        }

        const maxScroll = el.scrollWidth - el.clientWidth;
        this.canScrollModulesLeft = el.scrollLeft > 2;
        this.canScrollModulesRight = maxScroll > 2 && el.scrollLeft < maxScroll - 2;
    }

    private scrollActiveModuleIntoView(): void {
        const nav = this.moduleNav?.nativeElement;
        if (!nav || !this.activeModule) {
            return;
        }

        const active = nav.querySelector(
            `[data-module-key="${this.activeModule}"]`
        ) as HTMLElement | null;
        active?.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
            block: 'nearest',
        });
    }

    private applyPermissionsAndResolve(): void {
        if (this._authService.isCompanySelectionPending() || this._authService.isBranchSelectionPending()) {
            return;
        }

        const url = this._router.url;
        const path = url.split('?')[0];
        const firstSegment = path.split('/').filter(Boolean)[0] ?? '';

        if (ClassyLayoutComponent.AUTH_ROUTE_SEGMENTS.has(firstSegment)) {
            return;
        }

        this.allowedModules = this.modules
            .filter(m => this._permissionService.isModuleVisible(m.key as ModuleKey))
            .map(m => ({
                ...m,
                link:
                    this._permissionService.findFirstPermittedLinkInModule(m.key as ModuleKey) ??
                    m.link,
            }));
        this.headerModules = this.allowedModules.filter(m => m.showInHeader !== false);
        this.showNoPermissions = this.allowedModules.length === 0 && !this._permissionService.isSuperAdmin();
        this.scheduleModuleNavUpdate(true);
        if (this.showNoPermissions) {
            this.currentNavigation = [];
        } else if (this.navigation) {
            const allowedKeys = this.allowedModules.map(m => m.key);
            const currentModuleKey = this._permissionService.moduleKeyFromPath(path);
            if (
                currentModuleKey &&
                allowedKeys.length > 0 &&
                !allowedKeys.includes(currentModuleKey) &&
                !ClassyLayoutComponent.GLOBAL_ROUTE_SEGMENTS.has(firstSegment)
            ) {
                const target = this.allowedModules[0].link;
                if (path !== target) {
                    this._router.navigateByUrl(target);
                }
                return;
            }
            this.resolveNavigationFromUrl(url);
        }
    }

    private resolveNavigationFromUrl(url: string): void {
        if (!this.navigation) return;
        if (this.showNoPermissions) {
            this.currentNavigation = [];
            return;
        }
        const path = url.split('?')[0];
        const firstSegment = path.split('/').filter(Boolean)[0] ?? '';

        if (ClassyLayoutComponent.GLOBAL_ROUTE_SEGMENTS.has(firstSegment)) {
            // Keep the current module sidebar; do not treat this as a module switch.
            if (!this.currentNavigation?.length && this.navigation) {
                this.activeModule = 'accounting';
                this.currentNavigation = this._permissionService.filterNavigation(this.navigation.accounting);
            }
            return;
        }

        // Match on the first path segment only. Substring checks like
        // url.includes('/inventory') incorrectly treat Settings routes such as
        // /settings/reconciliation/inventory-status as the Inventory module.
        const moduleKey = this._permissionService.moduleKeyFromPath(path) ?? 'accounting';
        this.activeModule = moduleKey;
        let items = this._permissionService.filterNavigation(
            this.navigation[moduleKey] ?? this.navigation.accounting
        );
        if (moduleKey === 'serviceBilling' && !this.scbEnabled) {
            items = items.filter((item) => !item.meta?.['requiresScbEnabled']);
        }
        if (moduleKey === 'digitalInvoicing' && !this.diFbrSandbox) {
            items = items.filter((item) => !item.meta?.['requiresDiSandbox']);
        }
        this.currentNavigation = items;
        this._redirectIfNoModuleRoutes(path);
    }

    /** Redirect when the user has module access but no permitted sub-routes on the current URL. */
    private _redirectIfNoModuleRoutes(currentPath: string): void {
        const hasNavItems = this.currentNavigation.some(
            item => item.type !== 'divider' && item.type !== 'spacer'
        );
        if (hasNavItems) {
            return;
        }

        const target = this._permissionService.findFirstPermittedRoute();
        if (target && target !== currentPath) {
            this._router.navigateByUrl(target);
        }
    }


    ngOnDestroy(): void {
        this._moduleNavResizeObserver?.disconnect();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        if (this.hideSidebar) {
            return;
        }

        if (this.isScreenSmall) {
            const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

            if (navigation) {
                navigation.toggle();
            }

            return;
        }

        this.toggleNavigationAppearance();
    }

    private updateHideSidebar(url: string): void {
        const path = (url || '').split('?')[0];
        this.hideSidebar = path === '/my-home' || path.startsWith('/my-home/');
    }

    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
        localStorage.setItem(
            ClassyLayoutComponent.SIDEBAR_APPEARANCE_KEY,
            this.navigationAppearance
        );
    }

}
